import { useState } from "react";

import { toast } from "sonner";

import {
  useQuitarFotografia,
  useQuitarImagenDelExpediente,
  useRotularFotografia,
  useSubirFotografia,
  useSubirImagenDelExpediente,
} from "@/hooks/useInformes";
import { getApiErrorMessage } from "@/lib/api";
import {
  FOTOGRAFIAS_DE_ANEXOS,
  PLANOS_DE_ANEXOS,
  urlDeLaFotografia,
  urlDeLaImagenDelExpediente,
  type FotografiaDeInforme,
  type Informe,
} from "@/lib/informes";

import { EncabezadoNumerado } from "./EncabezadoNumerado";
import { GaleriaDeFotografias } from "./GaleriaDeFotografias";
import { TarjetaDeImagen, motivoDeRechazoDeLaImagen } from "./TarjetaDeImagen";

/**
 * La tarjeta «Anexos» de «Descripción de la propiedad»: los cuatro planos que
 * casi todo informe real adjunta al final y las dos galerías (fotos del
 * inmueble y vistas con dron), cada foto con su leyenda libre.
 *
 * SE GUARDA TODO SOLO, igual que los encuadres de arriba: una imagen es una
 * petición con su archivo y una leyenda se escribe al salir del campo. Por eso
 * la tarjeta no le pasa nada a la barra de «Guardar cambios» ni cuenta como
 * cambio sin guardar. Y por lo mismo trae sus propias mutaciones en vez de
 * recibirlas por props: no hay estado de formulario que compartir con la
 * pestaña.
 */
export function AnexosDelInforme({ informe }: { informe: Informe }) {
  const subirImagen = useSubirImagenDelExpediente(informe.id);
  const quitarImagen = useQuitarImagenDelExpediente(informe.id);
  const subirFotografia = useSubirFotografia(informe.id);
  const quitarFotografia = useQuitarFotografia(informe.id);
  const rotular = useRotularFotografia(informe.id);

  /* Qué bloque tiene una operación en vuelo, para apagar SOLO sus botones. */
  const [ocupado, setOcupado] = useState<string | null>(null);
  /* El rechazo del NAVEGADOR (tipo y peso), por bloque. */
  const [rechazos, setRechazos] = useState<Record<string, string>>({});

  function elegir(clave: string, archivo: File, subir: (archivo: File) => void | Promise<unknown>) {
    const motivo = motivoDeRechazoDeLaImagen(archivo);

    setRechazos((actuales) => ({ ...actuales, [clave]: motivo ?? "" }));

    return motivo === null ? subir(archivo) : undefined;
  }

  function avisar(error: unknown) {
    toast.error(getApiErrorMessage(error, "No pudimos guardar los cambios. Inténtalo de nuevo."));
  }

  return (
    <div className="space-y-5">
      <section
        id="descripcion-anexos"
        className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5"
      >
        <EncabezadoNumerado
          idDelTitulo="descripcion-anexos-titulo"
          titulo="Anexos"
          bajada="Los planos y las fotografías que el informe adjunta al final. Todo es opcional."
          acciones={
            <span className="bg-muted text-ink-muted rounded-[0.4375rem] px-2.5 py-[0.3125rem] text-[0.65625rem] font-bold">
              Se guarda al soltarla
            </span>
          }
        />

        <div className="mt-3.5 grid gap-5 md:grid-cols-2">
          {PLANOS_DE_ANEXOS.map(({ tipo, etiqueta }) => {
            const imagen = informe.imagenes?.[tipo] ?? null;

            return (
              <div key={tipo} className="space-y-2">
                <h3 className="text-body text-ink font-bold">{etiqueta}</h3>
                <TarjetaDeImagen
                  plana
                  claseDelRecuadro="h-40"
                  titulo={etiqueta}
                  src={imagen ? urlDeLaImagenDelExpediente(informe.id, tipo, imagen.hash) : null}
                  mime={imagen?.mime}
                  peso={imagen?.bytes}
                  ocupado={ocupado === tipo}
                  error={rechazos[tipo] || null}
                  onArchivo={(archivo) =>
                    elegir(tipo, archivo, (a) => {
                      setOcupado(tipo);
                      subirImagen.mutate(
                        { tipo, archivo: a },
                        {
                          onSuccess: () => toast.success("Imagen guardada."),
                          onError: avisar,
                          onSettled: () => setOcupado(null),
                        },
                      );
                    })
                  }
                  onQuitar={() => {
                    setOcupado(tipo);
                    quitarImagen.mutate(tipo, {
                      onSuccess: () => toast.success("Imagen quitada."),
                      onError: avisar,
                      onSettled: () => setOcupado(null),
                    });
                  }}
                />
              </div>
            );
          })}
        </div>
      </section>

      {FOTOGRAFIAS_DE_ANEXOS.map(({ tipo, etiqueta, ayuda }) => (
        <GaleriaDeFotografias
          key={tipo}
          titulo={etiqueta}
          ayuda={ayuda}
          fotografias={informe.fotografias?.[tipo] ?? []}
          urlDe={(foto: FotografiaDeInforme) => urlDeLaFotografia(informe.id, foto.id, foto.hash)}
          ocupado={ocupado === tipo}
          error={rechazos[tipo] || null}
          onArchivo={(archivo) =>
            elegir(tipo, archivo, async (a) => {
              setOcupado(tipo);
              try {
                await subirFotografia.mutateAsync({ tipo, archivo: a });
                toast.success("Fotografía guardada.");
              } catch (error) {
                avisar(error);
              } finally {
                setOcupado(null);
              }
            })
          }
          onQuitar={(fotografiaId) => {
            setOcupado(tipo);
            quitarFotografia.mutate(fotografiaId, {
              onSuccess: () => toast.success("Fotografía quitada."),
              onError: avisar,
              onSettled: () => setOcupado(null),
            });
          }}
          onLeyenda={(fotografiaId, leyenda) =>
            rotular.mutate(
              { fotografiaId, leyenda },
              {
                onSuccess: () => toast.success("Leyenda guardada."),
                onError: avisar,
              },
            )
          }
        />
      ))}
    </div>
  );
}
